import { useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  API_SETTLEMENT_APPROVALS,
  API_SETTLEMENT_BATCHES_DECIDE,
  API_SETTLEMENT_MY_APPROVALS,
} from "../../../utils/apis";
import {
  createColumn,
  createViewAction,
  formatCurrency,
  formatDateTime,
} from "../../../utils/helpers";
import { message } from "antd";
import { URL_SETTLEMENT_BATCHES } from "../../../utils/urls";
import { FormActions } from "../../../components/common/FormActions";
import {
  BooleanTags,
  StatusTag,
  TransactionStatusTag,
} from "../../../components/common/registerUtils";
import {
  APPROVE,
  APPROVED,
  REJECT,
  REJECTED,
  SEND_TO_FM,
} from "../../../utils/constants";
import useSet from "../../common/useSet";

export const useSettlementApprovals = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { error, success, uploadData } = useSet();

  const level = useMemo(() => {
    return location.state?.filterParams?.level || searchParams.get("level");
  }, [location.state, searchParams]);

  const tableColumns = () => [
    createColumn("batch_reference", true, "Batch Reference"),
    createColumn("beneficiary", true, "Beneficiary"),
    createColumn("status", true, "Status"),
    createColumn("total_amount", true, "Total Amount"),
    createColumn("transaction_count", true, "Transaction Count"),
    createColumn("current_approval_level", true, "Current Approval Level"),
    createColumn("created_at", true, "Created At"),
    createColumn("prepared_by", true, "Prepared By"),
    createColumn("actions", false),
  ];

  const getBatchActions = (item: any, currentLevel: string | null) => {
    const baseActions = [createViewAction(URL_SETTLEMENT_BATCHES, item?.id)];

    // Only show approve/reject if viewing a specific level
    if (currentLevel) {
      const itemLevel = item?.current_approval_level;
      const requiresFm = item?.requires_fm_approval;
      const approveLabel = itemLevel === 2 && requiresFm ? SEND_TO_FM : APPROVE;

      return [
        ...baseActions,
        {
          label: approveLabel,
          custom: true,
          showReasonField: true,
          modalTitle: `Confirm ${approveLabel}`,
          modalOkType: "primary" as const,
          handleCustom: (comments?: string) =>
            uploadData({
              api: API_SETTLEMENT_BATCHES_DECIDE,
              method: "POST",
              params: {
                batch_id: item?.id,
                approval_level: itemLevel,
                decision: APPROVED.toUpperCase(),
                comments,
              },
            }),
        },
        {
          label: REJECT,
          custom: true,
          showReasonField: true,
          modalTitle: "Confirm Reject",
          modalOkType: "danger" as const,
          handleCustom: (comments?: string) =>
            uploadData({
              api: API_SETTLEMENT_BATCHES_DECIDE,
              method: "POST",
              params: {
                batch_id: item?.id,
                approval_level: itemLevel,
                decision: REJECTED.toUpperCase(),
                comments,
              },
            }),
        },
      ];
    }

    return baseActions;
  };

  useEffect(() => {
    if (success) {
      message.success("Batch decision submitted successfully");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(error?.detail || "Failed to submit batch decision");
    }
  }, [success, error]);

  const tableData = (data: any[] = []) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      return {
        ...item,
        status: <TransactionStatusTag status={item?.status} />,
        total_amount: formatCurrency(item?.total_amount),
        beneficiary: item?.beneficiary?.name,
        created_at: formatDateTime(item?.created_at),
        transaction_count: item?.transaction_count || "0",
        requires_fm_approval: (
          <BooleanTags status={item?.requires_fm_approval} />
        ),
        prepared_by: item?.prepared_by?.name,
        actions: <FormActions actions={getBatchActions(item, level)} />,
      };
    });
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Batch Details",
          fields: {
            batch_reference: item?.batch_reference,
            status: <StatusTag status={item?.status} />,
            total_amount: formatCurrency(item?.total_amount),
            transaction_count: item?.transaction_count,
            requires_fm_approval: (
              <BooleanTags status={item?.requires_fm_approval} />
            ),
            is_locked: <BooleanTags status={item?.is_locked} />,
          },
        },
      ],
    };
  };

  // Build API params based on level
  const getApiParams = () => {
    if (level) {
      return { level: parseInt(level) };
    }
    return {};
  };

  const customApi = level
    ? API_SETTLEMENT_APPROVALS
    : API_SETTLEMENT_MY_APPROVALS;

  return {
    tableColumns,
    tableData,
    viewData,
    customApi,
    apiParams: getApiParams(),
  };
};
